import { useState } from 'react';
import { createSequence } from '@olivierpascal/helpers';
import { Stepper } from '@sixui/ui';

import type { IPackagePreparerStepProps } from '~/components/organisms/PackagePreparerStep';
import type { IContainerProps } from '~/components/utils/Container';
import {
  PackagePreparerAndroidThemeStep,
  PackagePreparerAppIconStep,
  PackagePreparerFinalStep,
  PackagePreparerNameStep,
  PackagePreparerOrganizationStep,
  PackagePreparerSplashScreenStep,
} from '~/components/organisms/PackagePreparerStep';

export type IPackagePreparerStepperProps = IContainerProps &
  IPackagePreparerStepProps;

export type IIosPrepareStepEvent = {
  source: 'init' | 'user';
};

export const PackagePreparerStepper: React.FC<IPackagePreparerStepperProps> = (
  props,
) => {
  const { sx, appId, onSuccess, platform } = props;
  const [activeStep, setActiveStep] = useState<number | undefined>(undefined);
  const [completedSteps, setCompletedSteps] = useState<Record<number, boolean>>(
    {},
  );

  const steps = [
    PackagePreparerOrganizationStep,
    PackagePreparerNameStep,
    PackagePreparerAppIconStep,
    ...(platform === 'android' ? [PackagePreparerAndroidThemeStep] : []),
    PackagePreparerSplashScreenStep,
    PackagePreparerFinalStep,
  ];

  const firstUncompletedStep = createSequence(steps.length - 1).reduce(
    (acc, step, index) => {
      if (acc === index && completedSteps[step]) {
        return step + 1;
      }

      return acc;
    },
    0,
  );

  const handleSuccess = (
    stepIndex: number,
    event: IIosPrepareStepEvent,
  ): void => {
    setCompletedSteps((completedSteps) => ({
      ...completedSteps,
      [stepIndex]: true,
    }));

    if (event.source === 'user') {
      setActiveStep((activeStep) => (activeStep ?? stepIndex) + 1);

      if (stepIndex === steps.length - 1 && onSuccess) {
        onSuccess({ source: 'user' });
      }
    }
  };

  return (
    <Stepper
      activeStep={activeStep ?? firstUncompletedStep}
      orientation="vertical"
      sx={sx}
    >
      {steps.map((Step, index) => (
        <Step
          key={index}
          onClick={() => setActiveStep(index)}
          disabled={firstUncompletedStep < index}
          onSuccess={(event) => handleSuccess(index, event)}
          appId={appId}
          platform={platform}
        />
      ))}
    </Stepper>
  );
};
